import type {
  RouteGuard,
  RouteGuardVerdict,
  RouteType,
} from "@intrasync/stores";

import { isAdmin as isAdminTier } from "../auth/tier";
import { routePermissionPath } from "../shared/permissionPath";
import type RootStore from "./RootStore";
import type { PageAccessStore } from "./PageAccessStore";

/**
 * The deny reason surfaced as `Unauthorized?reason=…`.
 *
 * The STRING VALUE is a wire format shared with
 * `components/Unauthorized/Unauthorized.tsx` (its `REASON_MESSAGES` key) and
 * with any bookmarked `…/unauthorized?reason=page_blocked` URL, so it keeps the
 * old "blocked" spelling on purpose even though the human-facing vocabulary is
 * now "hidden". Rename what a human reads, not the wire format.
 */
export const PAGE_BLOCKED_REASON = "page_blocked";

/**
 * HQ's per-user PAGE-VISIBILITY rule, expressed as a library {@link RouteGuard}.
 *
 * ─── THIS IS UX GATING, NOT A SECURITY BOUNDARY ───────────────────────────────
 *
 *   Page visibility hides pages in this app's UI. It is NOT a security
 *   boundary: what an account may read or change through the API is governed
 *   by its role/tier, which the server enforces on every request
 *   (`packages/api/src/services/_shared/rbac-tier.ts`).
 *
 * This guard runs IN THE BROWSER and nowhere else. Deny rows are keyed by web
 * route path, and no route→service mapping exists, so no API hook consults them
 * — pinned by `packages/api/src/services/user/permissions/
 * page-visibility-boundary.test.ts`. A user whose Infrastructure page is hidden
 * but who holds the `operator` tier can still call `instance-actions` directly.
 * Treat a deny here as "don't show them this screen", never as "they can't do
 * this".
 *
 * ─── FAIL-OPEN, DELIBERATELY ──────────────────────────────────────────────────
 *
 * Every uncertain branch below returns "allow": no `requiresAuth`, admin tier,
 * a missing `pageAccessStore`, an unloaded rule cache. The library also treats
 * a THROWING guard as allow. Both are the right default for UX gating — a
 * broken guard that hides every page turns a cosmetic feature into a total
 * lockout, and there is nothing to protect here because the API enforces the
 * real boundary regardless. For a security check the same behaviour would be
 * unacceptable: an authorization decision that cannot be computed must refuse,
 * not permit.
 *
 * ─── LIBRARY SEAM ─────────────────────────────────────────────────────────────
 *
 * This is the one piece of routing behaviour that did NOT come across for free
 * when the forked RouterStore was deleted: the library has no `pageAccessStore`
 * concept, and `routeGuards` is the seam it provides instead. The guard is
 * threaded RootStore → RouterStore → PermissionManager, and is evaluated by
 * both `checkRoutePermission` (the goTo gate) and `handleAuthReroute` (which
 * turns a deny into `Unauthorized?reason=page_blocked`) — exactly the two
 * places the fork's `PermissionManager.isRouteBlocked` was called.
 *
 * Semantics, preserved verbatim from the fork (default-VISIBLE):
 * - Hide rules never apply to unauthenticated surfaces (login, oauth): a route
 *   without `requiresAuth` is always allowed here.
 * - Admin-tier users can never have a page hidden from them. Admin is resolved
 *   through HQ's own tier helper (`auth/tier.isAdmin`, tier-first with an
 *   `adminUser` fallback) rather than `ctx.isAdmin`, because RouterStore does
 *   not forward `PermissionManagerOptions.isAdminResolver` — reading the tier
 *   helper directly is the only way to keep admin resolution single-sourced.
 * - Only an explicit `isDenied` rule whose location matches the route's
 *   permission path hides it. `routePermissionPath` keeps raw route
 *   definitions (nav builders, the Permissions authoring view) and compiled
 *   Route instances (the goTo gate) in the same layout-prefixed path space.
 */
export function createPageAccessRouteGuard(rootStore: RootStore): RouteGuard {
  return (route: RouteType): RouteGuardVerdict => {
    // Hide rules never apply to unauthenticated surfaces (login, oauth).
    if (!route?.requiresAuth) return "allow";
    // Admins never have a page hidden from them.
    if (isAdminTier(rootStore)) return "allow";

    const pageAccessStore = (
      rootStore as unknown as { pageAccessStore?: PageAccessStore }
    ).pageAccessStore;
    // FAIL-OPEN #2 (deliberate — see the docstring). No store mounted means no
    // rules are knowable, and an unknowable UX hint must not become a lockout.
    if (!pageAccessStore) return "allow";

    return pageAccessStore.isPathDenied(routePermissionPath(route))
      ? { deny: PAGE_BLOCKED_REASON }
      : "allow";
  };
}

export default createPageAccessRouteGuard;
